/**
 * useSubscriptions
 *
 * Reads the current user's exclusive-content subscriptions from the global
 * `exclusiveContent` Redux slice. The slice is kept in sync by
 * `ExclusiveContentListener`'s live Firestore listener, so this hook performs
 * no fetching of its own — it is a pure slice mirror.
 *
 * Mirrors Flutter's SubscriptionsScreen ordering: `updatedDate desc`, falling
 * back to `createdDate` when `updatedDate` is undefined.
 *
 * Web improvement over Flutter: drops subscriptions whose creator is in the
 * current user's `blockedUsers` / `usersBlockedMe`. The server already
 * inactivates both sides on block (see
 * `cloud_functions/functions/src/functions/user/block_user.ts:81-97`), but
 * this is defence-in-depth for the race window before that cleanup is
 * mirrored into the local slice.
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectExclusiveContentLoaded,
  selectExclusiveContentSubscriptions,
} from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import type { RootState } from '@/presentation/store';
import { shouldFilterOutBlockedUserUseCase } from '@/domain/usecases/user/shouldFilterOutBlockedUserUseCase';

export interface UseSubscriptionsResult {
  subscriptions: ExclusiveContentSubscriptionEntity[];
  isLoading: boolean;
}

export const useSubscriptions = (): UseSubscriptionsResult => {
  const subscriptions = useSelector(selectExclusiveContentSubscriptions);
  const isLoaded = useSelector(selectExclusiveContentLoaded);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Depend on the two blocked-user arrays' identity instead of the whole
  // `currentUser` reference — the auth listener mirrors SecureStorage on
  // every write, so `currentUser` churns reference much more often than
  // these arrays actually change.
  const blockedUsers = currentUser?.blockedUsers;
  const usersBlockedMe = currentUser?.usersBlockedMe;

  const visibleSubscriptions = useMemo(() => {
    // `userId` on a subscription row is the OTHER user — the creator the
    // current user is subscribed to. Same identity reference Flutter uses at
    // subscriptions_screen.dart, and the navigation target on tap.
    const filtered = subscriptions.filter(
      (subscription) => !shouldFilterOutBlockedUserUseCase.execute(subscription.userId, currentUser),
    );
    return filtered.sort((a, b) => {
      const aDate = a.updatedDate ?? a.createdDate;
      const bDate = b.updatedDate ?? b.createdDate;
      return bDate - aDate;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions, blockedUsers, usersBlockedMe]);

  return {
    subscriptions: visibleSubscriptions,
    isLoading: !isLoaded,
  };
};
